/**
 * Fetch Functions for Groups Management
 *
 * Uses server-computed fields and single-item reorder operations.
 *
 * API Endpoints:
 * - GET /groupsmanagement/list - Returns pre-sorted groups with computed fields
 * - POST /groupsmanagement/reorder - Single-item reorder (gap-based ordering)
 * - POST /group/{id}/delete - Delete a group
 *
 * Set USE_REAL_API = false to use mock data for development/testing.
 */

import FnrFetch3 from "Utilities/FnrFetch/FnrFetch3";
import { throwReadableError } from "Utilities/fnReportError";
import { getMockGroups } from "testUtils/mockData/navigation/groups";

/**
 * Feature flag: Set to true to use real API endpoints.
 * Set to false to use mock data for development.
 */
export const USE_REAL_API = true;

/**
 * Fetch all groups with computed fields, pre-sorted in display order.
 *
 * Response includes:
 * - section: "orphanedParents" | "grandparents"
 * - hierarchyLevel: 1 | 2 | 3 (1 = top-level, 2 = child, 3 = grandchild)
 * - hasChildren: boolean
 * - descendantIds: number[]
 * - canHaveSubgroups: boolean
 *
 * @returns {Promise<{ success: boolean, groups: Array }>}
 */
export const fetchGroups = async () => {
    if (!USE_REAL_API) {
        // Mock mode: Return mock data with simulated network delay
        await simulateNetworkDelay(100);
        return getMockGroups();
    }

    // Share a single in-flight request with legacy JS (flocknote.js), which
    // fetches the same /navigation/groups endpoint. Whichever side fires first
    // creates the promise on window._groupsFetchPromise; the other reuses it.
    if (window._groupsFetchPromise) {
        const promise = window._groupsFetchPromise;
        // Clear so subsequent refetches (after group changes) hit the network
        window._groupsFetchPromise = null;
        const groups = await promise;
        if (groups) return { groups };
    }

    const endpoint = `/navigation/groups`;
    const fetchFunction = new FnrFetch3(endpoint);

    // Store the promise so legacy can reuse if it hasn't fired yet
    const fetchPromise = fetchFunction.get(endpoint).catch((err) => {
        throwReadableError(endpoint, err);
    });
    window._groupsFetchPromise = fetchPromise.then((r) => r?.groups);

    const response = await fetchPromise;

    // Clear the window promise after resolution. Legacy only needs it during
    // initial page load (when !window.data.groups). Leaving a resolved promise
    // here means the NEXT fetchGroups call (e.g. from invalidateQueries after
    // a mutation) would reuse this stale data instead of hitting the network,
    // causing optimistic updates to be overwritten with pre-mutation data.
    window._groupsFetchPromise = null;

    if (response?.groups) {
        window.fnReactBus.setGroups(response.groups);
    }

    return response;
};

/**
 * Update a group's position (reorder).
 *
 * Uses gap-based ordering - only the moved group's position is updated.
 * Server calculates the appropriate arran value to insert between siblings.
 *
 * @param {number} groupId - The ID of the group to move
 * @param {number|null} afterGroupId - ID of the group to position after
 * @param {number|null} beforeGroupId - ID of the group to position before
 * @param {number|null} parentId - Optional new parent ID (for re-parenting)
 * @returns {Promise<{ success: boolean, groupId: number, newArran: number, parentChanged: boolean }>}
 */
export const reorderGroup = async (
    groupId,
    afterGroupId = null,
    beforeGroupId = null,
    parentId = null
) => {
    if (!USE_REAL_API) {
        // Mock mode: Simulate success
        await simulateNetworkDelay(150);
        return { success: true, groupId, newArran: 0, parentChanged: false };
    }

    // Real API mode
    const endpoint = `/groupsmanagement/reorder`;
    const postData = {
        groupId,
        ...(afterGroupId && { afterGroupId }),
        ...(beforeGroupId && { beforeGroupId }),
        ...(parentId !== null && { parentId }),
    };

    const fetchFunction = new FnrFetch3(endpoint);
    const response = await fetchFunction.post(endpoint, postData).catch((err) => {
        throwReadableError(endpoint, err);
    });

    return response;
};

/**
 * Delete a group.
 *
 * @param {number} groupID - The ID of the group to delete
 * @param {boolean} deleteFromParent - Whether to also remove members from parent groups
 * @returns {Promise<Object>}
 */
export const deleteGroup = async (groupID, deleteFromParent = false) => {
    const endpoint = `/group/${groupID}/delete`;
    const postData = {
        option: deleteFromParent ? "delete_members" : "keep_members",
    };

    const fetchFunction = new FnrFetch3(endpoint);
    const response = await fetchFunction.post(endpoint, postData).catch((err) => {
        throwReadableError(endpoint, err);
    });
    return response;
};

/**
 * Legacy full-list reorder (V1).
 * Used only for hierarchy promotion which still requires the old endpoint.
 *
 * @param {number} networkID - The network ID
 * @param {FormData} postData - The reorder form data
 * @returns {Promise<Object>}
 */
export const reorderGroupsLegacy = async (networkID, postData) => {
    const endpoint = `/network/${networkID}/reorder`;

    const fetchFunction = new FnrFetch3(endpoint);
    const response = await fetchFunction.post(endpoint, postData).catch((err) => {
        throwReadableError(endpoint, err);
    });
    return response;
};

/**
 * Simulate network delay for mock data.
 * @param {number} ms - Milliseconds to delay
 */
const simulateNetworkDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Helper to simulate an error response for testing error handling.
 * @param {string} message - Error message
 */
export const simulateError = async (message = "Simulated error") => {
    await simulateNetworkDelay(100);
    throw new Error(message);
};
